import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Linkedin, UserPlus, Mail, MessageCircle, Check, Send, RefreshCw, AlertCircle, Zap } from "lucide-react";
import { toast } from "sonner";

export default function LinkedInAutomation() {
  const [status, setStatus] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({}); // lead_id → 'invite'|'inmail'|'dm'|'draft'
  const [actions, setActions] = useState([]);
  const [autoSend, setAutoSend] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [draftsByLead, setDraftsByLead] = useState({}); // lead_id → { connect_note, inmail_subject, inmail_body, first_dm }
  const [expanded, setExpanded] = useState({}); // lead_id → bool

  const load = async () => {
    setLoading(true);
    try {
      const [s, ld, ac, asnd] = await Promise.all([
        api.get("/linkedin-auto/status"),
        api.get("/leads", { params: { limit: 200 } }),
        api.get("/linkedin-auto/actions", { params: { limit: 100 } }),
        api.get("/linkedin-auto/auto-settings"),
      ]);
      setStatus(s.data);
      setLeads(ld.data);
      setActions(ac.data);
      setAutoSend(!!asnd.data.linkedin_auto_send);
      // Pre-populate drafts from any leads that have them stored
      const dm = {};
      ld.data.forEach((l) => {
        if (l.linkedin_drafts) dm[l.lead_id] = l.linkedin_drafts;
      });
      setDraftsByLead(dm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoSend = async () => {
    setSavingToggle(true);
    try {
      const { data } = await api.post("/linkedin-auto/auto-settings", { linkedin_auto_send: !autoSend });
      setAutoSend(data.linkedin_auto_send);
      toast.success(data.linkedin_auto_send
        ? "🤖 Auto-send ON — scheduler will start dispatching invites + InMails every 5 min for approved leads"
        : "Auto-send OFF — no new automated sends will happen");
    } catch (err) {
      toast.error("Toggle failed");
    } finally {
      setSavingToggle(false);
    }
  };

  const runNow = async () => {
    try {
      await api.post("/linkedin-auto/run-now");
      toast.success("Manual tick started — refresh in 1-2 min to see results");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run-now failed");
    }
  };

  const aiDraft = async (lead) => {
    setSending((s) => ({ ...s, [lead.lead_id]: "draft" }));
    try {
      const { data } = await api.post(`/linkedin-auto/ai-draft/${lead.lead_id}`);
      setDraftsByLead((m) => ({ ...m, [lead.lead_id]: data }));
      setExpanded((m) => ({ ...m, [lead.lead_id]: true }));
      toast.success("AI drafted personalised messages for " + lead.full_name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed");
    } finally {
      setSending((s) => { const n = { ...s }; delete n[lead.lead_id]; return n; });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const connectLinkedIn = async () => {
    try {
      const { data } = await api.post("/linkedin-auto/hosted-auth");
      if (data.url) {
        window.open(data.url, "_blank", "noopener");
        toast.success("Opened Unipile login page in a new tab. After you log in, come back and refresh.");
      } else {
        toast.error("Could not generate hosted-auth URL");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hosted-auth failed");
    }
  };

  const sendAction = async (lead, kind, opts = {}) => {
    if (!lead.linkedin_url) {
      toast.error("Lead has no LinkedIn URL");
      return;
    }
    const ai = draftsByLead[lead.lead_id] || lead.linkedin_drafts || {};
    if (!ai.connect_note && !opts.dryRun) {
      if (!window.confirm(`No AI-drafted message yet for ${lead.full_name}. Continue with a generic message?\n\nRecommend: click 'AI Draft' first.`)) return;
    }
    if (!window.confirm(`${opts.dryRun ? "[DRY RUN] " : ""}Send ${kind} to ${lead.full_name}?\n\n${opts.dryRun ? "Nothing sent." : "WILL send a real LinkedIn action."}`)) return;

    setSending((s) => ({ ...s, [lead.lead_id]: kind }));
    try {
      const body = {
        lead_id: lead.lead_id,
        linkedin_url: lead.linkedin_url,
        dry_run: opts.dryRun || false,
      };
      if (kind === "invite") {
        body.message = ai.connect_note || `Hi ${lead.full_name?.split(" ")[0] || "there"}, came across your profile — open to connecting?`;
      } else if (kind === "inmail") {
        body.subject = ai.inmail_subject || `On ${lead.industry || "your industry"}`;
        body.text = ai.inmail_body || `Hi ${lead.full_name?.split(" ")[0] || "there"}, noticed your work at ${lead.company}. Happy to share a couple of observations we've picked up. Not a pitch — just thought you might find it useful.`;
      } else if (kind === "dm") {
        body.text = ai.first_dm || `Thanks for connecting, ${lead.full_name?.split(" ")[0] || "there"}. Quick context on why I reached out — open to comparing notes sometime?`;
      }
      const { data } = await api.post(`/linkedin-auto/${kind}`, body);
      if (data.dry_run) {
        toast.success(`[DRY RUN] ${kind} preview ready`);
      } else {
        toast.success(`✓ ${kind} sent to ${lead.full_name}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${kind} failed`);
    } finally {
      setSending((s) => {
        const n = { ...s };
        delete n[lead.lead_id];
        return n;
      });
    }
  };

  const connected = status?.connected;
  const profile = status?.profile;
  const dailyInv = status?.daily_invites || { used: 0, cap: 20 };
  const dailyMsg = status?.daily_messages || { used: 0, cap: 30 };
  const dailyInmail = status?.daily_inmails || { used: 0, cap: 5 };
  const dailyDm = status?.daily_dms || { used: 0, cap: 25 };

  // Group actions by lead_id for quick lookup
  const actionsByLead = {};
  actions.forEach((a) => {
    if (!a.lead_id) return;
    if (!actionsByLead[a.lead_id]) actionsByLead[a.lead_id] = [];
    actionsByLead[a.lead_id].push(a);
  });

  return (
    <Layout
      title="LinkedIn Automation"
      subtitle="Connect requests · Sales Nav InMail · 1st-degree DMs — auto-stops InMail when invite is accepted"
      actions={
        <>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="li-reload" style={{ borderRadius: 2 }}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
          </Button>
        </>
      }
    >
      {!connected && (
        <div className="border border-border bg-card p-6 mb-6" style={{ borderRadius: 2 }} data-testid="li-not-connected">
          <Linkedin className="h-8 w-8 text-primary mb-3" strokeWidth={1.5} />
          <h3 className="font-serif text-xl mb-2" style={{ fontWeight: 400 }}>Connect your LinkedIn</h3>
          <p className="text-sm text-muted-foreground mb-4">
            One-time login via Unipile's secure hosted page. After that GT Engine can send connect requests, Sales Nav InMails, and DMs on your behalf — at safe daily limits (20 invites + 5 InMails + 25 DMs).
          </p>
          <Button onClick={connectLinkedIn} data-testid="li-connect" style={{ borderRadius: 2 }}>
            <Linkedin className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Connect LinkedIn via Unipile
          </Button>
        </div>
      )}

      {connected && profile && (
        <div className="border border-border bg-card p-5 mb-6" style={{ borderRadius: 2 }} data-testid="li-status-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Linkedin className="h-4 w-4 text-primary" strokeWidth={1.5} />
                <span className="overline">Connected</span>
                {profile.premium && (
                  <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-primary text-primary-foreground" style={{ borderRadius: 2 }}>
                    Sales Nav
                  </span>
                )}
              </div>
              <div className="text-lg font-medium">{profile.name}</div>
              <div className="text-xs text-muted-foreground font-mono">@{profile.public_identifier} · {profile.country} proxy</div>
            </div>
            <div className="flex items-center gap-8 text-sm">
              <div>
                <div className="overline">Today's invites</div>
                <div className="text-lg font-mono mt-0.5" data-testid="li-daily-invites">
                  {dailyInv.used}<span className="text-muted-foreground text-xs">/{dailyInv.cap}</span>
                </div>
              </div>
              <div>
                <div className="overline">InMails (credits)</div>
                <div className="text-lg font-mono mt-0.5" data-testid="li-daily-inmails">
                  {dailyInmail.used}<span className="text-muted-foreground text-xs">/{dailyInmail.cap}</span>
                </div>
              </div>
              <div>
                <div className="overline">Today's DMs</div>
                <div className="text-lg font-mono mt-0.5" data-testid="li-daily-dms">
                  {dailyDm.used}<span className="text-muted-foreground text-xs">/{dailyDm.cap}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {connected && (
        <>
          {/* Automation toggle */}
          <div className="border border-border bg-card p-5 mb-6" style={{ borderRadius: 2 }} data-testid="li-auto-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="overline mb-1">Full automation</div>
                <div className="text-base font-medium">{autoSend ? "🤖 Auto-send ON" : "Auto-send OFF"}</div>
                <div className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  When ON: every 5 min the scheduler picks up to 20 approved leads/day, AI-drafts a unique personalised connect note for each, sends it.
                  After 24h with no acceptance, sends a different Sales Nav InMail. When a connection IS accepted, the InMail track stops automatically and a warm first-DM is sent. All actions paced with 90-300s human-like jitter.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={runNow} variant="outline" disabled={!autoSend} title={!autoSend ? "Turn auto-send ON first" : "Trigger one immediate tick"} data-testid="li-run-now" style={{ borderRadius: 2 }}>
                  <Zap className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Run now
                </Button>
                <Button onClick={toggleAutoSend} disabled={savingToggle} data-testid="li-auto-toggle" style={{ borderRadius: 2 }}>
                  {savingToggle ? <RefreshCw className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : autoSend ? "Turn OFF" : "Turn ON auto-send"}
                </Button>
              </div>
            </div>
          </div>

          <div className="overline mb-3">Leads · {leads.filter((l) => l.linkedin_url).length} with LinkedIn URL</div>
          <div className="bg-card border border-border" style={{ borderRadius: 2 }} data-testid="li-leads-table">
            {leads.filter((l) => l.linkedin_url).map((lead) => {
              const acts = actionsByLead[lead.lead_id] || [];
              const inviteAct = acts.find((a) => a.kind === "invite");
              const inmailAct = acts.find((a) => a.kind === "inmail");
              const dmAct = acts.find((a) => a.kind === "dm");
              const sendingKind = sending[lead.lead_id];
              const accepted = inviteAct?.status === "accepted" || lead.linkedin_connected;
              return (
                <div key={lead.lead_id} className="border-b border-border" data-testid={`li-lead-${lead.lead_id}`}>
                  <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{lead.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{lead.title} · {lead.company}</div>
                      <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground font-mono hover:text-foreground inline-flex items-center gap-1 mt-0.5">
                        <Linkedin className="h-2.5 w-2.5" strokeWidth={1.5} /> {lead.linkedin_url.replace("https://", "").replace("www.", "").slice(0, 40)}
                      </a>
                    </div>

                    {/* Status pills */}
                    <div className="flex items-center gap-1.5">
                      {draftsByLead[lead.lead_id] && (
                        <button
                          onClick={() => setExpanded((m) => ({ ...m, [lead.lead_id]: !m[lead.lead_id] }))}
                          className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-primary/10 text-primary hover:bg-primary/20"
                          style={{ borderRadius: 2 }}
                          title="View AI-drafted messages"
                          data-testid={`li-draft-toggle-${lead.lead_id}`}
                        >
                          AI {expanded[lead.lead_id] ? "▴" : "▾"}
                        </button>
                      )}
                      {inviteAct && (
                        <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${accepted ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`} style={{ borderRadius: 2 }} title={`Sent ${inviteAct.created_at}`}>
                          <UserPlus className="h-2.5 w-2.5 inline mr-0.5" strokeWidth={1.5} />
                          {accepted ? "Accepted" : "Sent"}
                        </span>
                      )}
                      {inmailAct && (
                        <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-muted-foreground" style={{ borderRadius: 2 }} title={`InMail sent ${inmailAct.created_at}`}>
                          <Mail className="h-2.5 w-2.5 inline mr-0.5" strokeWidth={1.5} /> InMail
                        </span>
                      )}
                      {dmAct && (
                        <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-secondary text-muted-foreground" style={{ borderRadius: 2 }} title={`DM sent ${dmAct.created_at}`}>
                          <MessageCircle className="h-2.5 w-2.5 inline mr-0.5" strokeWidth={1.5} /> DM
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5">
                      {!draftsByLead[lead.lead_id] && !inviteAct && (
                        <Button size="sm" variant="ghost" disabled={!!sendingKind} onClick={() => aiDraft(lead)} data-testid={`li-aidraft-${lead.lead_id}`} style={{ borderRadius: 2 }} title="AI-personalise the connect note + InMail + DM (uses your value prop + research)">
                          {sendingKind === "draft" ? <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <><Zap className="h-3 w-3 mr-1" strokeWidth={1.5} /> AI Draft</>}
                        </Button>
                      )}
                      {!inviteAct && (
                        <Button size="sm" variant="outline" disabled={!!sendingKind} onClick={() => sendAction(lead, "invite")} data-testid={`li-invite-${lead.lead_id}`} style={{ borderRadius: 2 }}>
                          {sendingKind === "invite" ? <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <><UserPlus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Connect</>}
                        </Button>
                      )}
                      {!accepted && profile?.premium && !inmailAct && (
                        <Button size="sm" variant="outline" disabled={!!sendingKind} onClick={() => sendAction(lead, "inmail")} data-testid={`li-inmail-${lead.lead_id}`} style={{ borderRadius: 2 }} title="Sales Nav InMail">
                          {sendingKind === "inmail" ? <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <><Mail className="h-3 w-3 mr-1" strokeWidth={1.5} /> InMail</>}
                        </Button>
                      )}
                      {accepted && !dmAct && (
                        <Button size="sm" disabled={!!sendingKind} onClick={() => sendAction(lead, "dm")} data-testid={`li-dm-${lead.lead_id}`} style={{ borderRadius: 2 }}>
                          {sendingKind === "dm" ? <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <><Send className="h-3 w-3 mr-1" strokeWidth={1.5} /> Send DM</>}
                        </Button>
                      )}
                      {accepted && <Check className="h-3.5 w-3.5 text-success" strokeWidth={2} />}
                    </div>
                  </div>

                  {/* AI Draft preview */}
                  {expanded[lead.lead_id] && draftsByLead[lead.lead_id] && (
                    <div className="px-4 pb-4 bg-secondary/20 border-t border-border" data-testid={`li-drafts-${lead.lead_id}`}>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3 text-xs">
                        <div className="bg-background border border-border p-3" style={{ borderRadius: 2 }}>
                          <div className="overline mb-1">Connect note ({draftsByLead[lead.lead_id].connect_note?.length || 0}/250)</div>
                          <div className="leading-relaxed">{draftsByLead[lead.lead_id].connect_note || "(empty)"}</div>
                        </div>
                        <div className="bg-background border border-border p-3" style={{ borderRadius: 2 }}>
                          <div className="overline mb-1">Sales Nav InMail</div>
                          <div className="font-medium mb-1">{draftsByLead[lead.lead_id].inmail_subject || "(no subject)"}</div>
                          <div className="leading-relaxed text-foreground/80">{draftsByLead[lead.lead_id].inmail_body || "(empty)"}</div>
                        </div>
                        <div className="bg-background border border-border p-3" style={{ borderRadius: 2 }}>
                          <div className="overline mb-1">First DM (after accept)</div>
                          <div className="leading-relaxed">{draftsByLead[lead.lead_id].first_dm || "(empty)"}</div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <button onClick={() => aiDraft(lead)} className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground hover:text-foreground" data-testid={`li-redraft-${lead.lead_id}`}>
                          <RefreshCw className="h-2.5 w-2.5 inline mr-1" strokeWidth={1.5} /> Re-draft
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {leads.filter((l) => l.linkedin_url).length === 0 && (
              <div className="px-6 py-8 text-xs text-muted-foreground text-center">
                No leads with LinkedIn URLs. Import some via Lead Finder → Lusha first.
              </div>
            )}
          </div>

          {/* Recent activity */}
          {actions.length > 0 && (
            <>
              <div className="overline mt-8 mb-3">Recent activity</div>
              <div className="bg-card border border-border" style={{ borderRadius: 2 }}>
                {actions.slice(0, 20).map((a) => (
                  <div key={a.action_id} className="border-b border-border px-4 py-2 flex items-center gap-3 text-xs">
                    <span className="font-mono text-muted-foreground w-32 shrink-0">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                    <span className="font-medium uppercase tracking-widest text-[10px] font-mono w-16">{a.kind}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${a.status === "accepted" ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"}`} style={{ borderRadius: 2 }}>
                      {a.status}
                    </span>
                    <span className="text-muted-foreground truncate flex-1">{a.message || a.text || a.subject || "—"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
